import { useFirmType } from '@folio/hooks/use-firm-type';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  type ActionsEnumKey,
  configControllerGetPublicProductConfigQueryOptions,
} from 'korint-sdk';
import { type PolicyQuickAction, useKorintConfig } from 'korint-sdk/react';
import { useMemo } from 'react';
import { getVisibleQuickActions } from './getVisibleQuickActions';

/** Apps outside uniq have no `uniqAppConfig`: they hand their actions over as an argument. */
export function useVisiblePolicyQuickActions(
  actions?: ReadonlyArray<PolicyQuickAction>,
): PolicyQuickAction[] {
  const { korintConfig } = useKorintConfig();
  const { firmType, user } = useFirmType();
  const { policy } = usePolicyOnDashboard();

  const quickActions = actions ?? korintConfig.uniqAppConfig?.policyDashboard?.quickActions;

  // Custom and link actions name their own permissions, so the fetched set follows the config.
  const requestedActions = useMemo(
    () => [
      ...new Set<ActionsEnumKey>([
        'PUSH_POLICY_EVENT',
        'EDIT_POLICY',
        'EDIT_ASSET',
        'EDIT_CUSTOMER',
        ...(quickActions ?? []).flatMap(action =>
          action.type === 'custom' || action.type === 'link' ? (action.requiredActions ?? []) : [],
        ),
      ]),
    ],
    [quickActions],
  );

  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: requestedActions,
  });

  const { data: declaredPolicyEvents = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: ({ customEvents }) => customEvents?.policy ?? [],
  });

  return useMemo(
    () =>
      getVisibleQuickActions(quickActions, {
        productId: policy.productId,
        status: policy.status,
        can,
        declaredPolicyEvents,
        firmType,
        isSuperUser: user?.isSuperUser,
      }),
    [
      quickActions,
      policy.productId,
      policy.status,
      can,
      declaredPolicyEvents,
      firmType,
      user?.isSuperUser,
    ],
  );
}
